// The sidebar's grouped navigation (groups, rows and hubs in navModel.ts).
// A hub is one row for several pages (Projects, Agents, Automations):
// it highlights on any of them, and the top bar shows their tabs
// (HubTabs). Each row shows as navRow says for the workspace's switches.
//
// Rows are 36px (30px at 701-750px tall, 28px on the shortest) and span
// the sidebar edge to edge (no corners), muted text; hover raises the row, the current page gets the
// shared selected look (./selectedItem: selected surface, full text
// colour, 3px accent bar flush with the sidebar's left edge, the full
// height of the row, so bar and row read as one). Icons and labels line
// up with the workspace name above (24px). A count
// sits at the right where something waits (useNavCounts; tones and pill
// looks from features/attention/tones), read out after the item's name.
//
// Below 1024px, or when the operator collapses it, the sidebar is a 64px
// rail (see CustomerSidebar; Tailwind's `rail:` variant): rows
// show their icon only, with the name as a tooltip, group headings become
// hairlines and a count becomes a dot on the icon.
//
// Plain <Link>s with an explicit aria-current: NavLink would compute
// "current" from its own `to`, which disagrees with the match lists
// (/code/deployments is Code, /connections redirects into it).

import { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

import { useMediaQuery } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";
import { useUi } from "@/stores/ui";

import { COUNT_DOT_TONE, COUNT_PILL_TONE } from "@/features/attention/tones";

import { NAV_GROUPS, drawnRows, matchesPath, type NavRow } from "./navModel";
import { SELECTED_BAR, SELECTED_ROW } from "./selectedItem";
import { useNavCounts, type NavCount } from "./useNavCounts";
import { useFirstDraw, useNavFlags } from "./useNavFlags";

/** Below this the rail is forced (the media half of Tailwind's `rail:`). */
export const RAIL_QUERY = "(max-width: 1023.98px)";

/** Whether the sidebar shows as the icon rail: forced by the width, or collapsed. */
export function useRail(): boolean {
  const narrow = useMediaQuery(RAIL_QUERY);
  const collapsed = useUi((s) => s.sidebarCollapsed);
  return narrow || collapsed;
}

export function SidebarNav({ customerId }: { customerId: string }) {
  const { pathname } = useLocation();
  // The workspace's switches: live once the settings answer, before that
  // what this browser remembered for the workspace (./navFlags), so a
  // returning operator's nav is whole on its first paint.
  const flags = useNavFlags(customerId);
  // A first visit in this browser knows nothing yet: the rows that need a
  // switch keep their places unseen until the switches arrive, at most
  // 1.2 s, and the rest draws at once, so no row grows in the middle.
  const ready = useFirstDraw(flags !== undefined);
  const counts = useNavCounts(customerId || null);
  const rail = useRail();
  const navRef = useRef<HTMLElement>(null);

  // When the nav scrolls (a short window), keep the current page's row in
  // view. Only the nav's own scrollTop moves — scrollIntoView would also
  // scroll the overflow-hidden shell around it.
  useEffect(() => {
    const nav = navRef.current;
    const row = nav?.querySelector<HTMLElement>("[aria-current='page']");
    if (!nav || !row) return;
    const n = nav.getBoundingClientRect();
    const r = row.getBoundingClientRect();
    if (r.top < n.top) nav.scrollTop -= n.top - r.top + 8;
    else if (r.bottom > n.bottom) nav.scrollTop += r.bottom - n.bottom + 8;
  }, [pathname, flags, ready]);

  return (
    <nav
      ref={navRef}
      aria-label="Main"
      aria-busy={ready ? undefined : true}
      className="min-h-0 flex-1 overflow-y-auto pb-[12px] [scrollbar-width:thin] [@media(max-height:700px)]:pb-[8px]"
    >
      {NAV_GROUPS.map((group, gi) => {
        // `=== true` (navRow) so a switch not known yet never flashes a
        // gated row in; while a first visit waits its place is held.
        const rows = drawnRows(group.items, flags, !ready);
        if (rows.length === 0) return null;
        const headingId = group.label ? `nav-group-${gi}` : undefined;
        return (
          <div key={group.label ?? "top"} role="group" aria-labelledby={headingId}>
            {group.label && (
              <>
                <div
                  id={headingId}
                  className={cn(
                    "block px-[24px] pb-[4px] pt-[16px] text-xs font-medium text-fg-subtle rail:sr-only",
                    "[@media(min-height:701px)_and_(max-height:750px)]:pt-[8px] [@media(max-height:700px)]:pt-[6px]",
                  )}
                >
                  {group.label}
                </div>
                <div aria-hidden className="mx-[20px] my-[8px] hidden border-t border-line-subtle rail:block" />
              </>
            )}
            <ul className="flex flex-col">
              {rows.map(({ row, held }) => (
                <li key={row.key} className={held ? "invisible" : undefined} aria-hidden={held || undefined}>
                  <NavLinkRow
                    row={row}
                    rail={rail}
                    active={row.match.some((p) => matchesPath(pathname, p))}
                    count={row.count ? counts[row.count] : undefined}
                  />
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}

function NavLinkRow({ row, active, count, rail }: { row: NavRow; active: boolean; count?: NavCount; rail: boolean }) {
  const Icon = row.icon;
  return (
    <Link
      to={row.to}
      aria-current={active ? "page" : undefined}
      // The rail hides the name, so it becomes the tooltip; a count always
      // says on hover what it counts ("Todos (32 todos due, 29 overdue)").
      title={count ? `${row.label} (${count.label})` : rail ? row.label : undefined}
      className={cn(
        "group relative flex h-[36px] items-center gap-3 px-[24px] text-sm font-medium transition-colors",
        "[@media(min-height:701px)_and_(max-height:750px)]:h-[30px] [@media(max-height:700px)]:h-[28px]",
        "rail:justify-center rail:gap-0 rail:px-0",
        "focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus",
        active
          ? cn(SELECTED_ROW, SELECTED_BAR, "before:bottom-0 before:top-0 before:rounded-none")
          : "text-fg-muted [@media(hover:hover)]:hover:bg-raised [@media(hover:hover)]:hover:text-fg",
      )}
    >
      <span className="relative flex shrink-0">
        <Icon
          aria-hidden
          className={cn(
            "h-4 w-4 transition-colors",
            active ? "text-fg" : "text-fg-subtle [@media(hover:hover)]:group-hover:text-fg",
          )}
          strokeWidth={1.75}
        />
        {count && (
          <span
            aria-hidden
            className={cn(
              "absolute -right-[7px] -top-[6px] hidden h-[8px] w-[8px] rounded-full shadow-[0_0_0_2px_var(--c-bg)] rail:block",
              COUNT_DOT_TONE[count.tone],
            )}
          />
        )}
      </span>
      <span className="min-w-0 flex-1 truncate rail:sr-only">{row.label}</span>
      {count && (
        <>
          <span
            aria-hidden
            className={cn(
              "inline-grid h-[20px] min-w-[20px] shrink-0 place-items-center rounded-pill px-[6px] text-xs font-semibold tabular-nums rail:hidden",
              COUNT_PILL_TONE[count.tone],
            )}
          >
            {count.value > 99 ? "99+" : count.value}
          </span>
          <span className="sr-only">, {count.label}</span>
        </>
      )}
    </Link>
  );
}
